/**
 * The `change_throughout` section of a change-throughout card, field for field the
 * core's `ChangeThroughoutCardSummary` (`apps/core/redline/change_throughout.py`): the
 * words and what they become, every place found in the range split three ways (the
 * places the card changes, the places the agent left out, the places the web handed
 * back), and the reader's reading of every paragraph that holds a place, over which a
 * set of places the user checks is applied (`changeThroughoutProposalOf`).
 *
 * Snake case, as the card's other top-level fields are (`table_of_contents_redirects`);
 * the paragraphs keep the reader's camel case (`documentParagraphSchema`).
 */
import { z } from "zod";

import { documentParagraphSchema } from "./document-paragraph";
import { redlineTextCardSchema } from "./redline-payload";
import { tableCellPathSchema } from "./table-cell-path";

/**
 * One place the range reads as the words to change.
 *
 * `match_id` names it while its paragraph reads the same (`match_id_of` in the core).
 * `occurrence` is its place among the stretches of its paragraph that read as the
 * words, counted from 1: the `occurrence` its `replace_span` names. `text_before` and
 * `text_after` are up to six words either side.
 *
 * EMPTY
 *   `text_before` is "" when the place opens its paragraph, `text_after` when it closes
 *   it; `table_cell_path` is [] outside every table.
 *
 * NULL
 *   `heading_path` is null for a paragraph before the document's first heading.
 */
export const changeThroughoutMatchSchema = z.object({
    match_id: z.string().min(1),
    paragraph_id: z.string().min(1),
    occurrence: z.number().int().positive(),
    text: z.string(),
    text_before: z.string(),
    text_after: z.string(),
    heading_path: z.array(z.string()).nullable(),
    table_cell_path: tableCellPathSchema,
});

export type ChangeThroughoutMatch = z.infer<typeof changeThroughoutMatchSchema>;

/** A place whose `replace_span` the web handed back, with the web's reason enum and its sentence. */
export const matchTheWebHandedBackSchema = z.object({
    match: changeThroughoutMatchSchema,
    reask_reason: z.string(),
    reask_message: z.string(),
});

export type MatchTheWebHandedBack = z.infer<typeof matchTheWebHandedBackSchema>;

export const changeThroughoutCardSummarySchema = z.object({
    text_now: z.string().min(1),
    text_wanted: z.string().min(1),
    changed_matches: z.array(changeThroughoutMatchSchema),
    matches_left_out_by_the_agent: z.array(changeThroughoutMatchSchema),
    matches_the_web_handed_back: z.array(matchTheWebHandedBackSchema),
    paragraphs: z.array(documentParagraphSchema),
});

export type ChangeThroughoutCardSummary = z.infer<typeof changeThroughoutCardSummarySchema>;

/** The whole `change_throughout` tool result: a text card with the change's summary beside it. */
export const changeThroughoutCardSchema = redlineTextCardSchema.extend({
    change_throughout: changeThroughoutCardSummarySchema,
});

export type ChangeThroughoutCard = z.infer<typeof changeThroughoutCardSchema>;
